<script>
// Popover da aba de Macros renderizado FORA da árvore do sidebar.
//
// O problema nunca foi z-index: entre o popover e a viewport há TRÊS ancestrais
// que RECORTAM por overflow, e recorte não se resolve reordenando pintura —
//   1. ConversationBox.vue  `.conversation-sidebar-wrap`  overflow-hidden
//   2. ContactPanel.vue     `.contact--panel`             overflow-hidden
//   3. ContactPanel.vue     o scrollport dos cards        overflow-y-auto
//      (com o eixo Y em `auto` o eixo X deixa de ser `visible` por regra do
//      CSS, então corta nos quatro lados)
//
// E há um quarto que aprisiona: `.conversation-details-wrap` declara
// `container-type: inline-size`, que implica contenção de layout — a caixa vira
// contexto de empilhamento E bloco contentor até dos descendentes
// `position: fixed`. Por isso nem `fixed` resolve DENTRO da árvore: as
// coordenadas passariam a ser relativas àquele container.
//
// A saída é o elemento não descender de nada disso. Mesmo padrão do
// components/ui/ContextMenu.vue: move o próprio $el para document.body no mount
// e se posiciona a partir do retângulo do gatilho.
const GAP_Y = 6; // respiro entre o gatilho e o popover
const GAP_X = 32; // recuo à esquerda da coluna de botões (o antigo `right-8`)
const EDGE = 8; // respiro mínimo da borda da janela

export default {
  props: {
    // Elemento-gatilho (a linha da macro/fluxo). É dele que sai a âncora.
    anchor: {
      type: typeof HTMLElement === 'undefined' ? Object : HTMLElement,
      default: null,
    },
  },
  data() {
    return {
      position: { top: '0px', left: '0px' },
    };
  },
  mounted() {
    document.body.appendChild(this.$el);
    this.reposition();
    window.addEventListener('resize', this.reposition);
    // `true` (captura): o scroll que move o gatilho é o do scrollport interno
    // do sidebar, que não borbulha até a window.
    window.addEventListener('scroll', this.reposition, true);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.reposition);
    window.removeEventListener('scroll', this.reposition, true);
  },
  destroyed() {
    // O v-if do pai destrói o componente, mas o nó agora vive em document.body:
    // sem isto ele fica órfão na página.
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  methods: {
    reposition() {
      if (!this.anchor) return;
      const rect = this.anchor.getBoundingClientRect();
      const { offsetWidth: width, offsetHeight: height } = this.$el;
      const { innerWidth, innerHeight } = window;

      const maxLeft = Math.max(EDGE, innerWidth - width - EDGE);
      const wanted = rect.right - GAP_X - width;
      const left = Math.min(Math.max(EDGE, wanted), maxLeft);

      // Abre para cima, como antes. Sem espaço acima, cai para baixo; sem
      // espaço nos dois, encosta no topo e o max-height do conteúdo rola.
      let top = rect.top - GAP_Y - height;
      if (top < EDGE) {
        const below = rect.bottom + GAP_Y;
        top = below + height <= innerHeight - EDGE ? below : EDGE;
      }

      this.position = { top: `${top}px`, left: `${left}px` };
    },
  },
};
</script>

<template>
  <div class="popover-portal fixed z-[9999]" :style="position">
    <slot />
  </div>
</template>

<style scoped lang="scss">
/* O "sopro" lateral que era a transição `result-blow` do FlowItem. Vira
   animação CSS porque uma <transition> do Vue não sobrevive ao
   re-parenteamento do elemento no mount. */
.popover-portal {
  animation: popover-portal-in 0.18s cubic-bezier(0.2, 0.9, 0.3, 1.3);
}

@keyframes popover-portal-in {
  from {
    opacity: 0;
    transform: translateX(14px) scale(0.94);
  }
}
</style>
